import { Capacitor } from '@capacitor/core'
import {
  Geolocation,
  PermissionStatus,
  PositionOptions,
  WatchPositionCallback,
} from '@capacitor/geolocation'
import { getDistance } from 'geolib'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Maybe } from '@/clients/utoniq-core/schema'

export type Location = {
  latitude: number
  longitude: number
}

const checkPermission = async () => {
  const { location } =
    Capacitor.getPlatform() === 'web'
      ? await checkAndRequestPermissionForWeb()
      : await Geolocation.checkPermissions()

  return location === 'granted'
}

export const requestPermissions = async () => {
  const permission = await checkPermission()

  if (permission) return permission
  if (Capacitor.getPlatform() === 'web') return permission

  const { location } = await Geolocation.requestPermissions()

  return location === 'granted'
}

export const watchPosition = async (
  opt: PositionOptions,
  callback: WatchPositionCallback
) => {
  return await Geolocation.watchPosition(opt, callback)
}

/**
 * @description
 * 現在地を監視・取得するhooks
 */
export const useLocationWatcher = () => {
  const callbackIdRef = useRef<string>()
  const [permission, setPermission] = useState(false)
  const [currentLocation, setCurrentLocation] =
    useState<Maybe<Partial<Location>>>(null)

  const requestPermission = useCallback(async () => {
    const permission = await requestPermissions()
    setPermission(permission)
  }, [])

  const watch = useCallback(
    async (opt: PositionOptions = {}) => {
      if (!permission) return
      callbackIdRef.current = await watchPosition(opt, r => {
        setCurrentLocation({
          latitude: r?.coords?.latitude,
          longitude: r?.coords?.longitude,
        })
      })
    },
    [permission]
  )

  const unwatch = useCallback(async () => {
    if (!callbackIdRef.current) return
    await Geolocation.clearWatch({ id: callbackIdRef.current })
  }, [])

  return { permission, currentLocation, watch, unwatch, requestPermission }
}

/**
 * @description
 * web geolocation APIのpermission確認用
 * Geolocation.checkPermissions()にwebの確認機能がないので、capacitorのpluginを模倣して実装
 * webのgeolocation APIは現在地取得時にpermissionのrequestとcheckを同時にするので、getCurrentPositionの結果でpermissionの有無を確認する
 * (類似機能にweb permissions APIはSafari非対応のため使用しない)
 *
 * ref: https://capacitorjs.com/docs/plugins/web
 */
const checkAndRequestPermissionForWeb = (): Promise<PermissionStatus> => {
  return Geolocation.getCurrentPosition()
    .then(
      r =>
        ({ location: 'granted', coarseLocation: 'granted' } as PermissionStatus)
    )
    .catch(e => {
      /**
       * ユーザーキャンセルの場合、エラーは投げずステータスを返す
       */
      if (e.message === 'User denied Geolocation')
        return { location: 'denied', coarseLocation: 'denied' }

      throw e
    })
}

/**
 * @description
 * 2点間の緯度・経度がradiusの範囲内かどうかを確認するhook
 *
 * @param radius 判定距離（メートル）
 * @returns {Boolean}
 */
export const useIsInside = ({
  locations,
  radius,
}: {
  locations: [Maybe<Partial<Location>>, Maybe<Partial<Location>>]
  radius?: Maybe<number>
}) => {
  const [isInside, setIsInside] = useState<boolean>(false)

  useEffect(() => {
    // 存在チェック
    if (
      locations.some(location => !location?.latitude || !location?.longitude) ||
      !radius
    )
      return

    const distance = getDistance(
      locations[0] as Location,
      locations[1] as Location
    )

    setIsInside(radius >= distance)
  }, [locations, radius])

  return { isInside }
}
